<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import ActionMenu from '@/components/common/ActionMenu.vue'
import { createDate } from '@/composables/getDateText'
import { getNumberString } from '@/composables/getNumberString'

const props = defineProps({
  comment: { required: true }
})

const store = useStore()

const colors = ref(['#00AA55', '#009FD4', '#B381B3', '#939393', '#E3BC00', '#D47500', '#DC2A2A'])
const loading = ref(false)
const edit = ref(false)
const actionDropdown = ref({
  edit: {
    icon: 'mdi-pencil',
    title: 'Edit',
    action: 'edit',
    disabled: false
  },
  delete: {
    icon: 'mdi-delete',
    title: 'Delete',
    action: 'delete',
    disabled: false
  }
})

const currentUser = computed(() => store.state.users.currentUser)
const commentObject = computed(() => store.state.comment.commentObject)

const canEdit = computed(() => currentUser.value.id === props.comment.user)
const editable = computed(() => props.comment.id && edit.value)
const disabled = computed(() => currentUser.value.id !== props.comment.user)

const cardBorderStyle = computed(() =>
  border.value ? `border-left: 5px solid #0186A7` : `border-left: 5px solid transparent`
)
const border = computed(() =>
  commentObject.value ? commentObject.value.id === props.comment.id : null
)

const createdDate = computed(() => {
  let date = new Date(props.comment.date_published)
  return createDate(date)
})
const modifiedDate = computed(() => {
  let date = new Date(props.comment.last_modified)
  return createDate(date)
})

const createdTime = computed(() => {
  let date = new Date(props.comment.date_published)
  return getNumberString(date.getHours()) + ':' + getNumberString(date.getMinutes())
})
const modifiedTime = computed(() => {
  let date = new Date(props.comment.last_modified)
  return getNumberString(date.getHours()) + ':' + getNumberString(date.getMinutes())
})

const userShortName = computed(() => {
  let split_name = props.comment.user_name.split(' ')
  if (split_name.length === 1) {
    return split_name[0][0]
  } else {
    return `${split_name[0][0]}${split_name.at(-1)[0]}`
  }
})

const numberFromText = (text) => {
  const charCodes = text
    .split('')
    .map((char) => char.charCodeAt(0))
    .join('')
  return parseInt(charCodes, 10)
}
const getColor = (text) => colors.value[numberFromText(text) % colors.value.length]

const deleteItem = (id) => store.dispatch('comment/deleteItem', id)
const updateComment = (payload) => store.dispatch('comment/updateComment', payload)
const setParameter = (payload) => store.dispatch('comment/setParameter', payload)
const setCommentObject = (payload) => store.dispatch('comment/setCommentObject', payload)

const actionDelete = (event) => {
  if (event === 'delete') {
    deleteItem(props.comment.id)
  }
  if (event === 'edit') {
    edit.value = !edit.value
  }
}

const toggleEdit = () => {
  actionDropdown.value.edit.icon = edit.value ? 'mdi-pencil-off' : 'mdi-pencil'
  actionDropdown.value.edit.title = edit.value ? 'Cancel' : 'Edit'
}

const update = async (e) => {
  const value = e.target.value
  loading.value = true
  setParameter({ item: props.comment, parameter: 'text', value })
  try {
    await updateComment(props.comment)
  } catch (error) {
    console.log(error)
  }
  edit.value = false
  loading.value = false
}

watch(edit, toggleEdit)

onMounted(() => {
  if (props.comment.created) {
    edit.value = true
  }
})
</script>
<template>
  <v-card
    class="ml-0"
    rounded="0"
    variant="flat"
    :loading="loading"
    @mouseover="setCommentObject(comment)"
    style="border-radius: 0"
  >
    <template v-if="loading" v-slot:loader>
      <v-progress-linear color="primary" height="3" indeterminate></v-progress-linear>
    </template>
    <v-divider></v-divider>

    <v-container class="pa-0" :style="cardBorderStyle">
      <v-row no-gutters>
        <v-col cols="1">
          <v-avatar :color="getColor(userShortName)" :size="36" class="ma-3 mb-0">
            {{ userShortName }}
          </v-avatar>
        </v-col>
        <v-col cols="9">
          <v-card-text class="pt-2 pb-0" style="color: black">
            <div>
              <strong>{{ comment.user_name }}</strong> <br />
              <div class="v-card-subtitle pl-0 d-inline">
                {{ createdTime }} {{ createdDate }}

                <div class="d-inline">|</div>
                Last modified at
                {{ modifiedTime }} {{ modifiedDate }}
              </div>
            </div>
          </v-card-text>
        </v-col>
        <v-col cols="2">
          <ActionMenu
            v-if="!disabled"
            :action-dropdown="actionDropdown"
            class-string="mt-2 mr-2 float-right"
            @dialog="actionDelete"
          ></ActionMenu>
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-2 pb-4 pr-3">
        <v-col cols="1"></v-col>
        <v-col>
          <v-card-text class="pa-0 pl-1 pr-4">
            <v-textarea
              ref="textarea"
              hide-details
              style="font-size: 14px"
              rows="3"
              auto-grow
              :variant="editable ? 'filled' : null"
              :readonly="!canEdit || !editable"
              color="primary"
              density="compact"
              :model-value="comment.text"
              @change="update"
            />
          </v-card-text>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>
